// src/contexts/AuthContext.tsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { userService, type MeDto } from "../services/userService";
import { getGaClientId, setAnalyticsUser } from "../lib/analytics";

type AuthContextValue = {
  user: MeDto | null;
  isAuthed: boolean;
  authReady: boolean,
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  googleLoginWithIdToken: (idToken: string) => Promise<void>;
  updateUser: (partial: Partial<MeDto>) => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const KEY = "loggedInUser"; // UI-only cache (niet security)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<MeDto | null>(null);
  const [authReady, setAuthReady] = useState(false);

  // (optioneel) laad meteen UI cache, zodat sidebar direct iets toont
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(KEY);
      if (raw) setUser(JSON.parse(raw));
    } catch {
      sessionStorage.removeItem(KEY);
    }
  }, []);

  // Always verify cookie session on initial mount so cookie-based sessions
  // work reliably across tabs and after sessionStorage is cleared.
  useEffect(() => {
    refreshAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cumulatieve user_type voor analytics: historie ophalen (ooit geboden /
  // ooit een listing gemaakt) en doorgeven aan de dataLayer. analytics.ts
  // pusht daarbij het user_data_ready-event en upgradet in-sessie bij een
  // eerste bod/listing. Tracking mag auth nooit blokkeren.
  useEffect(() => {
    if (!authReady) return;
    if (!user) {
      setAnalyticsUser(undefined);
      return;
    }
    let cancelled = false;
    (async () => {
      const activity = await userService.myActivity().catch(() => null);
      if (cancelled) return;
      setAnalyticsUser(
        user.id ?? undefined,
        activity
          ? {
              hasBids: activity.has_bids,
              hasListings: activity.has_listings,
              hasPurchases: activity.has_purchases,
              hasApprovedListings: activity.has_approved_listings,
            }
          : undefined,
      );
    })();

    // GA4 client_id naar de backend voor server-side Measurement Protocol-
    // events. De _ga-cookie wordt door de GA4-tag (via GTM) gezet en kan er
    // bij een eerste bezoek nog niet zijn — dan één late nieuwe poging.
    // Tracking mag auth nooit blokkeren; fouten worden stil genegeerd.
    const sendClientId = () => {
      const clientId = getGaClientId();
      if (clientId) {
        userService.saveGaClientId(clientId).catch(() => {});
        return true;
      }
      return false;
    };
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    if (!sendClientId()) {
      retryTimer = setTimeout(() => {
        if (!cancelled) sendClientId();
      }, 4000);
    }

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, authReady]);

  const login = async (username: string, password: string) => {
    const me = await userService.signIn({ email: username, password });
    setUser(me);
    sessionStorage.setItem(KEY, JSON.stringify(me));
    setAuthReady(true);
  };

  const googleLoginWithIdToken = async (idToken: string) => {
    const me = await userService.googleLogin(idToken);
    setUser(me);
    sessionStorage.setItem(KEY, JSON.stringify(me));
  };

  const logout = async () => {
    try {
      await userService.signOut();
    } finally {
      setUser(null);
      sessionStorage.removeItem(KEY);
    }
  };

  const refreshAuth = async () => {
    try {
      const me = await userService.me();
      setUser(me);
      sessionStorage.setItem(KEY, JSON.stringify(me));
    } catch {
      setUser(null);
      sessionStorage.removeItem(KEY);
    } finally {
      setAuthReady(true)
    }
  };

  const updateUser = useCallback((partial: Partial<MeDto>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...partial };
      sessionStorage.setItem(KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const value = useMemo(() => (
      {
        user,
        isAuthed: !!user,
        authReady,
        login,
        logout,
        refreshAuth,
        googleLoginWithIdToken,
        updateUser,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, authReady, updateUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth moet binnen <AuthProvider /> gebruikt worden");
  return ctx;
}
